import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import RangeSlider from "./RangeSlider";
import { SCOUTING_FILTERS } from "../../utils/scoutingFilters";

/**
 * Bottom sheet of range filters for the Scouting table.
 *
 * Edits apply live, so the result count on the button is the real count for
 * the ranges currently set. There is no separate apply step: the button
 * dismisses the sheet, it does not commit anything.
 *
 * Only filters with derived bounds are rendered. A column where every team
 * shares one value, or where the season's data has not landed yet, has no
 * usable range and is left out rather than shown as a dead control.
 */
export default function TeamFilterSheet({
  open,
  onClose,
  bounds,
  ranges,
  onChangeRange,
  onReset,
  resultCount,
}) {
  useEffect(() => {
    if (!open) return;
    const { style } = document.body;
    const prev = style.overflow;
    style.overflow = "hidden";
    return () => { style.overflow = prev; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const visible = SCOUTING_FILTERS.filter((f) => bounds[f.key]);

  // .theme-auto for the same reason as the other portals: custom properties
  // inherit down the real DOM, and a portal mounts outside the page that
  // opened it. Scouting is not migrated yet, so this changes nothing today and
  // will be right when it is.
  return createPortal(
    <div className="theme-auto" style={{ position: "fixed", inset: 0, zIndex: 10000 }}>
      <div
        onClick={onClose}
        style={{ position: "absolute", inset: 0, backgroundColor: "rgba(0,0,0,0.4)" }}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label="Filter teams"
        style={{
          position: "absolute", left: 0, right: 0, bottom: 0,
          backgroundColor: "#FFFFFF",
          borderTopLeftRadius: 24, borderTopRightRadius: 24,
          maxHeight: "88vh", display: "flex", flexDirection: "column",
          paddingBottom: "env(safe-area-inset-bottom)",
          boxShadow: "0 -8px 32px rgba(0,0,0,0.18)",
        }}
      >
        {/* Grabber */}
        <div style={{ display: "flex", justifyContent: "center", paddingTop: 10 }}>
          <div style={{ width: 44, height: 5, borderRadius: 999, backgroundColor: "#E5E7EB" }} />
        </div>

        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "12px 20px 4px",
        }}>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: "#111827", margin: 0 }}>
            Filter teams
          </h2>
          <button
            onClick={onClose}
            aria-label="Close filters"
            style={{
              border: "none", background: "none", cursor: "pointer",
              padding: 8, color: "#6B7280", lineHeight: 0,
            }}
          >
            <X size={22} />
          </button>
        </div>

        <div style={{ overflowY: "auto", padding: "8px 20px 20px", flex: 1 }}>
          {visible.length === 0 && (
            <p style={{ fontSize: 14, color: "#6B7280", margin: "16px 0" }}>
              No filters available yet. Preseason numbers load closer to the season.
            </p>
          )}

          {visible.map((filter) => {
            const bound = bounds[filter.key];
            const range = ranges[filter.key] || [bound.min, bound.max];

            return (
              <div key={filter.key} style={{ marginBottom: 28 }}>
                <div style={{
                  display: "flex", alignItems: "baseline",
                  justifyContent: "space-between", marginBottom: 14, gap: 12,
                }}>
                  <span style={{
                    fontSize: 13, fontWeight: 700, color: "#6B7280",
                    textTransform: "uppercase", letterSpacing: 0.4,
                  }}>
                    {filter.label}
                  </span>
                  <span style={{ fontSize: 17, fontWeight: 800, color: "#111827", whiteSpace: "nowrap" }}>
                    {filter.format(range[0])} – {filter.format(range[1])}
                  </span>
                </div>

                <RangeSlider
                  min={bound.min}
                  max={bound.max}
                  step={filter.step}
                  value={range}
                  label={filter.label}
                  onChange={(next) => onChangeRange(filter.key, next)}
                />
              </div>
            );
          })}
        </div>

        <div style={{
          display: "flex", gap: 12, padding: "12px 20px 20px",
          borderTop: "1px solid #F3F4F6", backgroundColor: "#FFFFFF",
        }}>
          <button
            onClick={onReset}
            style={{
              flex: 1, padding: "14px 0", borderRadius: 14,
              border: "1.5px solid #E5E7EB", backgroundColor: "#FFFFFF",
              color: "#111827", fontSize: 16, fontWeight: 700, cursor: "pointer",
            }}
          >
            Reset
          </button>
          <button
            onClick={onClose}
            style={{
              flex: 2, padding: "14px 0", borderRadius: 14, border: "none",
              backgroundColor: "#2563EB", color: "#FFFFFF",
              fontSize: 16, fontWeight: 700, cursor: "pointer",
            }}
          >
            {resultCount === 1 ? "Show 1 team" : `Show ${resultCount} teams`}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
